import { MockHook } from '@jupiterone/jupiter-web-toolkit';
import { useState, ChangeEvent, MouseEvent, KeyboardEvent } from 'react';

export interface PermissionTypeSelectProps {
  value?: string;
  permissionTypes: string[];
  onChange: (a: string) => void;
}

export const CUSTOM = 'custom-permission-id';

const useCustomPermission = () => {
  const [currentCustomPermission, setCurrentCustomPermission] = useState('');
  const [active, setActive] = useState(false);
  const activate = () => setActive(true);
  const deactivate = () => setActive(false);

  return {
    get current() {
      return currentCustomPermission;
    },
    set current(value: string) {
      setCurrentCustomPermission(value);
    },
    activate,
    deactivate,
    active,
  };
};

const usePermissionTypeSelectLogic = ({
  value,
  permissionTypes,
  onChange,
}: PermissionTypeSelectProps) => {
  const [selectOpen, setSelectOpen] = useState(false);
  const customPermission = useCustomPermission();

  const handleSelectChange = (e: ChangeEvent<any>) => {
    const value = e.target.value as string;
    if (value !== CUSTOM) {
      onChange(value);
      select.close();
    }
  };

  const handleSelectAutocompleteCancel = (e: KeyboardEvent) => {
    // prevent keys from being handled by the parent <Select>
    e.stopPropagation();
  };

  const handleCustomPermissionAdd = () => {
    onChange(customPermission.current);
    customPermission.deactivate();
    select.close();
  };

  const handleCustomPermissionChange = (e: ChangeEvent<HTMLInputElement>) => {
    customPermission.current = e.target.value;
  };

  const handleCustomPermissionActivate = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    customPermission.activate();
  };

  const select = {
    get isOpen() {
      return selectOpen;
    },
    open: () => {
      setSelectOpen(true);
    },
    close: () => {
      setSelectOpen(false);
    },
  };

  return {
    value,
    permissionTypes,
    select,
    customPermission,
    handleSelectChange,
    handleSelectAutocompleteCancel,
    handleCustomPermissionAdd,
    handleCustomPermissionChange,
    handleCustomPermissionActivate,
  };
};

export type PermissionTypeSelectComponentProps = ReturnType<
  typeof usePermissionTypeSelectLogic
>;

export const MOCK_PERMISSION_TYPE_SELECT_PROPS: PermissionTypeSelectComponentProps = {
  value: 'Test type',
  permissionTypes: ['_integrationType', 'customType'],
  select: {
    isOpen: false,
    open: MockHook.mockFunction,
    close: MockHook.mockFunction,
  },
  customPermission: {
    current: '',
    activate: MockHook.mockFunction,
    deactivate: MockHook.mockFunction,
    active: false,
  },
  handleSelectChange: MockHook.mockFunction,
  handleSelectAutocompleteCancel: MockHook.mockFunction,
  handleCustomPermissionAdd: MockHook.mockFunction,
  handleCustomPermissionChange: MockHook.mockFunction,
  handleCustomPermissionActivate: MockHook.mockFunction,
};
export default MockHook.useMockHook(
  usePermissionTypeSelectLogic,
  MOCK_PERMISSION_TYPE_SELECT_PROPS
);
